/**
 * Procedural faces. Features (eyes, lids, brows, nose, mouth, cheeks, tears) are signed-distance shapes drawn by the
 * character shader in "face space": a planar frame fixed to the head in rest pose (origin between the eyes, +u to the
 * character's left, +v up, unit = eye spacing), so they stay crisp at any zoom and ride the head bone. The base
 * texture's painted eyes are replaced locally with fur colours sampled from the same texture. Sculpted noses and
 * muzzles remain intact; a local inked mouth supplies visible expressions and speech on every species.
 *
 * This module holds the editable face (`FaceShape`), emotions (target deltas), the animator (blinks, saccades,
 * talking by visemes) and the packing into the shader uniform.
 */
import type { V3 } from './rig';

export interface FaceFrame {
  /** Point between the eyes on the face surface (rest, normalised model space). */
  center: V3;
  /** Eye spacing (centre to centre) in model heights. */
  scale: number;
  /** Optional face tilt (forward vector); default +z. */
  forward?: V3;
}

/** Everything the face editor changes (the "identity"); emotions add on top. */
import type { FaceShape } from '../../shared/atelier';
export type { FaceShape } from '../../shared/atelier';

export const IRIS_COLORS: { id: string; name: string; rgb: [number, number, number] }[] = [
  { id: 'amber', name: 'Янтарь', rgb: [0.78, 0.55, 0.16] },
  { id: 'green', name: 'Зелень', rgb: [0.42, 0.62, 0.22] },
  { id: 'azure', name: 'Лазурь', rgb: [0.25, 0.48, 0.78] },
  { id: 'copper', name: 'Медь', rgb: [0.72, 0.36, 0.14] },
  { id: 'gold', name: 'Золото', rgb: [0.86, 0.72, 0.25] },
  { id: 'ghost', name: 'Бельмо', rgb: [0.78, 0.8, 0.82] },
];

export const DEFAULT_SHAPE: FaceShape = {
  eyeSpacing: 1, eyeY: 0, eyeSize: 0.3, eyeAspect: 0.72, eyeTilt: 0.06,
  irisSize: 0.74, pupilSize: 0.42, pupilSlit: 0.35, irisColor: [0.78, 0.55, 0.16],
  lidHeavy: 0.2, bags: 0.06, derp: 0,
  browY: 0, browAngle: -0.25, browThickness: 0.026, browCurve: 0.25, browLength: 0.36,
  noseY: 0, noseSize: 0.13, noseColor: [0.86, 0.52, 0.5],
  mouthY: 0, mouthWidth: 0.42, mouthSmile: -0.25, fangs: 0,
  blush: 0, whiskerDots: 0.6,
  eraseRadius: 1, eraseY: 0, naturalMuzzle: true,
};

/** Sliders of the face editor. */
export const FACE_SLIDERS: { key: keyof FaceShape; name: string; min: number; max: number; group: string }[] = [
  { key: 'eyeSize', name: 'Размер', min: 0.18, max: 0.46, group: 'Глаза' },
  { key: 'eyeSpacing', name: 'Интервал', min: 0.75, max: 1.3, group: 'Глаза' },
  { key: 'eyeY', name: 'Высота', min: -0.2, max: 0.25, group: 'Глаза' },
  { key: 'eyeAspect', name: 'Круглость', min: 0.45, max: 1.0, group: 'Глаза' },
  { key: 'eyeTilt', name: 'Наклон', min: -0.35, max: 0.35, group: 'Глаза' },
  { key: 'irisSize', name: 'Радужка', min: 0.4, max: 0.9, group: 'Глаза' },
  { key: 'pupilSize', name: 'Зрачок', min: 0.15, max: 0.8, group: 'Глаза' },
  { key: 'pupilSlit', name: 'Щель', min: 0, max: 1, group: 'Глаза' },
  { key: 'lidHeavy', name: 'Веки', min: 0, max: 0.65, group: 'Глаза' },
  { key: 'bags', name: 'Мешки', min: 0, max: 1, group: 'Глаза' },
  { key: 'derp', name: 'Косоглазие', min: 0, max: 1, group: 'Глаза' },
  { key: 'browY', name: 'Высота', min: -0.12, max: 0.2, group: 'Брови' },
  { key: 'browAngle', name: 'Излом', min: -0.7, max: 0.6, group: 'Брови' },
  { key: 'browThickness', name: 'Толщина', min: 0, max: 0.11, group: 'Брови' },
  { key: 'browCurve', name: 'Дуга', min: -0.6, max: 0.9, group: 'Брови' },
  { key: 'browLength', name: 'Длина', min: 0.2, max: 0.55, group: 'Брови' },
  { key: 'noseSize', name: 'Нос', min: 0.06, max: 0.24, group: 'Морда' },
  { key: 'noseY', name: 'Нос выше', min: -0.6, max: 0.15, group: 'Морда' },
  { key: 'mouthWidth', name: 'Рот', min: 0.25, max: 0.75, group: 'Морда' },
  { key: 'mouthY', name: 'Рот выше', min: -0.6, max: 0.15, group: 'Морда' },
  { key: 'eraseY', name: 'Маска ниже', min: -0.4, max: 0.2, group: 'Морда' },
  { key: 'mouthSmile', name: 'Улыбка', min: -0.8, max: 0.8, group: 'Морда' },
  { key: 'fangs', name: 'Клыки', min: 0, max: 1, group: 'Морда' },
  { key: 'whiskerDots', name: 'Усы-точки', min: 0, max: 1, group: 'Морда' },
  { key: 'blush', name: 'Румянец', min: 0, max: 1, group: 'Морда' },
  { key: 'eraseRadius', name: 'Маска', min: 0.7, max: 1.35, group: 'Морда' },
];

/** Live expression channels (emotion + animation), added to / multiplied with the shape. */
export interface Expression {
  lidClose: number;      // 0 open … 1 closed (both)
  lidAngle: number;      // + angry slant (inner corners down), − sad
  lowerLid: number;      // squint (lower lid rises)
  eyeWide: number;       // eyes open wider (surprise)
  pupil: number;         // pupil scale delta (fear: small, love: big)
  browUp: number;        // both brows up
  browAngle: number;     // + angry V, − worried
  mouthOpen: number;     // 0 … 1
  mouthRound: number;    // 0 wide … 1 "O"
  mouthSmile: number;    // corners
  fangs: number;
  tongue: number;
  blush: number;
  tear: number;
  sweat: number;
  headTilt: number;
}

const ZERO: Expression = { lidClose: 0, lidAngle: 0, lowerLid: 0, eyeWide: 0, pupil: 0, browUp: 0, browAngle: 0, mouthOpen: 0, mouthRound: 0, mouthSmile: 0, fangs: 0, tongue: 0, blush: 0, tear: 0, sweat: 0, headTilt: 0 };

export const EMOTIONS: { id: string; name: string; e: Partial<Expression> }[] = [
  { id: 'calm', name: 'Смиренный', e: {} },
  { id: 'happy', name: 'Весёлый', e: {lowerLid:.42,lidClose:.04,mouthSmile:1,mouthOpen:.28,browUp:.09,blush:.3,headTilt:-.04} },
  { id: 'focused', name: 'Сосредоточенный', e: {lidClose:.16,lidAngle:.23,browAngle:.5,browUp:-.07,pupil:-.12,mouthSmile:.18} },
  { id: 'determined', name: 'Решительный', e: {lowerLid:.25,browAngle:.65,mouthSmile:.45,headTilt:-.07} },
  { id: 'curious', name: 'Любопытный', e: {eyeWide:.25,browUp:.12,mouthOpen:.14,mouthRound:.35,headTilt:.15} },
  { id: 'smug', name: 'Довольный', e: { lidClose: 0.32, lowerLid: 0.25, mouthSmile: 0.75, browAngle: -0.1, blush: 0.25, headTilt: 0.08 } },
  { id: 'angry', name: 'Гневный', e: { lidAngle: 0.55, lowerLid: 0.3, browAngle: 0.75, browUp: -0.08, mouthOpen: 0.55, mouthRound: 0.1, mouthSmile: -0.5, fangs: 1, pupil: -0.15 } },
  { id: 'sad', name: 'Скорбный', e: { lidAngle: -0.5, lidClose: 0.15, browAngle: -0.75, browUp: 0.06, mouthSmile: -0.8, tear: 1, headTilt: -0.12 } },
  { id: 'surprised', name: 'Изумлённый', e: { eyeWide: 0.35, pupil: -0.25, browUp: 0.14, mouthOpen: 0.6, mouthRound: 0.9 } },
  { id: 'scared', name: 'Испуганный', e: { eyeWide: 0.3, pupil: -0.45, lidAngle: -0.3, browAngle: -0.6, browUp: 0.1, mouthOpen: 0.3, mouthRound: 0.3, mouthSmile: -0.5, sweat: 1 } },
  { id: 'sleepy', name: 'Сонный', e: { lidClose: 0.62, browUp: -0.04, mouthSmile: -0.1, headTilt: 0.18 } },
  { id: 'love', name: 'Влюблённый', e: { pupil: 0.45, lidClose: 0.2, mouthSmile: 0.5, blush: 1, browAngle: -0.3, browUp: 0.05 } },
  { id: 'disgust', name: 'Брезгливый', e: { lidClose: 0.25, lowerLid: 0.45, lidAngle: 0.2, browAngle: 0.45, mouthSmile: -0.7, mouthOpen: 0.15, tongue: 1 } },
];

// ── visemes ─────────────────────────────────────────────────────────────────────────────────────────────────────
interface Viseme { open: number; round: number; smile: number }
const V_REST: Viseme = { open: 0, round: 0, smile: 0 };
const V: Record<string, Viseme> = {
  A: { open: 0.85, round: 0.1, smile: 0.1 },
  O: { open: 0.6, round: 0.95, smile: 0 },
  U: { open: 0.35, round: 1, smile: 0 },
  E: { open: 0.45, round: 0, smile: 0.35 },
  I: { open: 0.3, round: 0, smile: 0.5 },
  M: { open: 0, round: 0.2, smile: -0.05 },
  F: { open: 0.12, round: 0, smile: 0.1 },
  C: { open: 0.22, round: 0.15, smile: 0.05 }, // generic consonant
};
function visemeOf(ch: string): { v: Viseme; ms: number; vowel: boolean } | null {
  const c = ch.toLowerCase();
  if ('аяá'.includes(c) || c === 'a') return { v: V.A, ms: 120, vowel: true };
  if ('оё'.includes(c) || c === 'o') return { v: V.O, ms: 120, vowel: true };
  if ('уюы'.includes(c) || c === 'u' || c === 'w') return { v: V.U, ms: 110, vowel: true };
  if ('эе'.includes(c) || c === 'e') return { v: V.E, ms: 110, vowel: true };
  if ('и'.includes(c) || c === 'i' || c === 'y') return { v: V.I, ms: 100, vowel: true };
  if ('мбп'.includes(c) || 'mbp'.includes(c)) return { v: V.M, ms: 80, vowel: false };
  if ('фв'.includes(c) || 'fv'.includes(c)) return { v: V.F, ms: 75, vowel: false };
  if (/[a-zа-я]/.test(c)) return { v: V.C, ms: 60, vowel: false };
  if (' ,'.includes(c)) return { v: V_REST, ms: c === ',' ? 220 : 70, vowel: false };
  if ('.!?…;:—-'.includes(c)) return { v: V_REST, ms: 320, vowel: false };
  return null;
}

interface Key { t: number; v: Viseme; vowel: boolean; char: number }
export function visemeTrack(text: string): { keys: Key[]; duration: number } {
  const keys: Key[] = [];
  let t = 0.08;
  [...text].forEach((ch, i) => {
    const vz = visemeOf(ch);
    if (!vz) return;
    keys.push({ t, v: vz.v, vowel: vz.vowel, char: i });
    t += vz.ms / 1000;
  });
  keys.push({ t, v: V_REST, vowel: false, char: text.length });
  return { keys, duration: t + 0.25 };
}

// ── animator ────────────────────────────────────────────────────────────────────────────────────────────────────
const damp = (x: number, target: number, rate: number, dt: number) => x + (target - x) * (1 - Math.exp(-rate * dt));

export class FaceAnimator {
  emotion = 'calm';
  intensity = 1;
  readonly cur: Expression = { ...ZERO };
  private nextBlink = 1.5;
  private blinkT = -1;
  private doubleBlink = false;
  gaze: [number, number] = [0, 0];
  private gazeTarget: [number, number] = [0, 0];
  private nextSaccade = 0.6;
  private talk: { keys: Key[]; duration: number; t: number; text: string } | null = null;
  private mouth = { open: 0, round: 0, smile: 0 };
  /** Head motion extras while talking (nods), read by the body animation. */
  nod = 0;
  /** Characters of the current line revealed so far (for the speech scroll), −1 when silent. */
  revealed = -1;
  private seed = 1;
  private rand(): number { this.seed = (this.seed * 16807) % 2147483647; return this.seed / 2147483647; }

  say(text: string): void { this.talk = { ...visemeTrack(text), t: 0, text }; }
  get speaking(): string | null { return this.talk ? this.talk.text : null; }

  update(dt: number, lookYaw: number, lookPitch: number): void {
    const em = EMOTIONS.find((e) => e.id === this.emotion)?.e ?? {};
    const k = this.intensity;
    // emotion channels ease in
    for (const key of Object.keys(ZERO) as (keyof Expression)[]) {
      if (key === 'lidClose' || key === 'mouthOpen' || key === 'mouthRound') continue;
      this.cur[key] = damp(this.cur[key], (em[key] ?? 0) * k, 9, dt);
    }
    // blinks: 2–6 s apart, 150 ms, sometimes double
    this.nextBlink -= dt;
    if (this.nextBlink <= 0 && this.blinkT < 0) { this.blinkT = 0; this.doubleBlink = this.rand() < 0.2; this.nextBlink = 2 + this.rand() * 4; }
    let blink = 0;
    if (this.blinkT >= 0) {
      this.blinkT += dt;
      const b = this.blinkT;
      blink = b < 0.06 ? b / 0.06 : b < 0.15 ? 1 - (b - 0.06) / 0.09 : 0;
      if (b > 0.15) { if (this.doubleBlink) { this.blinkT = -0.12; this.doubleBlink = false; } else this.blinkT = -1; }
      if (this.blinkT < -0.5) this.blinkT = -1;
    } else if (this.blinkT < -0.01 && this.blinkT > -1) { this.blinkT += dt; if (this.blinkT >= 0) this.blinkT = 0; }
    const lidTarget = Math.max((em.lidClose ?? 0) * k, blink);
    this.cur.lidClose = blink > 0 ? lidTarget : damp(this.cur.lidClose, lidTarget, 14, dt);
    // gaze: follow the head look with small saccades
    this.nextSaccade -= dt;
    if (this.nextSaccade <= 0) {
      this.gazeTarget = [lookYaw * 0.9 + (this.rand() - 0.5) * 0.35, lookPitch * 0.6 + (this.rand() - 0.5) * 0.18];
      this.nextSaccade = 0.4 + this.rand() * 1.6;
      if (this.talk) this.gazeTarget = [this.gazeTarget[0] * 0.3, this.gazeTarget[1] * 0.3];
    }
    this.gaze[0] = damp(this.gaze[0], this.gazeTarget[0], 30, dt);
    this.gaze[1] = damp(this.gaze[1], this.gazeTarget[1], 30, dt);
    // talking: viseme track with co-articulation (look a little ahead, ease)
    let mt = { open: (em.mouthOpen ?? 0) * k, round: (em.mouthRound ?? 0) * k, smile: 0 };
    this.nod = damp(this.nod, 0, 6, dt);
    if (this.talk) {
      const tk = this.talk;
      tk.t += dt;
      const t = tk.t + 0.03;
      let i = 0;
      while (i < tk.keys.length - 1 && tk.keys[i + 1].t <= t) i++;
      const a = tk.keys[i], b = tk.keys[Math.min(i + 1, tk.keys.length - 1)];
      const f = b.t > a.t ? Math.min(1, (t - a.t) / (b.t - a.t)) : 0;
      const ease = f * f * (3 - 2 * f);
      mt = { open: a.v.open + (b.v.open - a.v.open) * ease, round: a.v.round + (b.v.round - a.v.round) * ease, smile: a.v.smile + (b.v.smile - a.v.smile) * ease };
      mt.open = Math.max(mt.open * 0.85, (em.mouthOpen ?? 0) * 0.5);
      if (a.vowel && f < 0.3) this.nod = Math.max(this.nod, 0.5 * (1 - f / 0.3) * (i % 3 === 0 ? 1 : 0.4));
      this.revealed = a.char;
      if (tk.t > tk.duration) { this.talk = null; this.revealed = -1; }
    }
    this.mouth.open = damp(this.mouth.open, mt.open, 22, dt);
    this.mouth.round = damp(this.mouth.round, mt.round, 18, dt);
    this.mouth.smile = damp(this.mouth.smile, mt.smile, 18, dt);
    this.cur.mouthOpen = this.mouth.open;
    this.cur.mouthRound = this.mouth.round;
    this.cur.mouthSmile = this.cur.mouthSmile + this.mouth.smile;
    if (this.talk) this.cur.browUp += this.nod * 0.04;
  }
}

/** Pack shape + expression into the shader's FaceParams (14 × vec4). */
export function packFace(out: Float32Array, o: number, s: FaceShape, e: Expression, gaze: [number, number], skinTopUv: [number, number], skinBottomUv: [number, number], enabled: boolean, debug: boolean): void {
  const eyeScale = 1 + e.eyeWide * 0.25;
  out.set([s.eyeSpacing, s.eyeY, s.eyeSize * eyeScale, s.eyeAspect * (1 + e.eyeWide * 0.2)], o);
  out.set([s.eyeTilt, s.irisSize, Math.max(0.08, s.pupilSize * (1 + e.pupil)), s.pupilSlit], o + 4);
  out.set([...s.irisColor, s.bags], o + 8);
  out.set([gaze[0], gaze[1], Math.min(1, s.lidHeavy * (1 - e.eyeWide * 1.5) + e.lidClose * (1 - s.lidHeavy * 0.5)), 0], o + 12);
  out.set([e.lidAngle, e.lowerLid, s.derp, 0], o + 16);
  out.set([s.browY + e.browUp, s.browAngle + e.browAngle, s.browThickness, s.browCurve], o + 20);
  out.set([s.browLength, s.noseY, s.noseSize, s.whiskerDots], o + 24);
  out.set([s.mouthY, s.mouthWidth * (1 - e.mouthRound * 0.45) * (1 + Math.max(0,e.mouthSmile) * .3), e.mouthOpen, e.mouthRound], o + 28);
  out.set([Math.max(-1, Math.min(1, s.mouthSmile + e.mouthSmile)), Math.max(s.fangs, e.fangs), e.tongue, Math.max(s.blush, e.blush)], o + 32);
  out.set([e.tear, e.sweat, enabled ? 1 : 0, debug ? 1 : 0], o + 36);
  out.set([skinTopUv[0], skinTopUv[1], skinBottomUv[0], skinBottomUv[1]], o + 40);
  out.set([...s.noseColor, s.eraseRadius], o + 44);
  out.set([s.eraseY, 0, s.naturalMuzzle ? 1 : 0, 0], o + 48);
}
export const FACE_VEC4S = 13;

/** Per-model default face (tuned to echo each base model's painted character). */
export function defaultShape(modelId: string, fit: { eyeSize: number; noseY: number; mouthY: number; eraseY: number } | null = null): FaceShape {
  const s: FaceShape = { ...DEFAULT_SHAPE, irisColor: [...DEFAULT_SHAPE.irisColor], noseColor: [...DEFAULT_SHAPE.noseColor] };
  if (modelId.startsWith('ink')) { s.irisColor = [0.25, 0.48, 0.78]; s.eyeAspect = 0.62; s.lidHeavy = 0.26; s.browAngle = -0.35; s.mouthSmile = -0.45; s.noseColor = [0.82, 0.62, 0.66]; }
  if (modelId === 'tabby-st') { s.eyeSize = 0.33; s.irisSize = 0.8; s.irisColor = [0.66, 0.45, 0.14]; s.lidHeavy = 0.17; s.browThickness = 0.018; s.bags = 0.06; }
  if (modelId === 'tabby-71s') {
    s.eyeSize = 0.36; s.eyeAspect = 0.78; s.irisSize = 0.84; s.irisColor = [0.55, 0.35, 0.12]; s.lidHeavy = 0.12; s.bags = 0.04; s.browThickness = 0.018;
    s.noseY = -0.5; s.noseSize = 0.115; s.noseColor = [0.58, 0.32, 0.28]; s.mouthY = -0.48; s.eraseY = -0.28; s.eraseRadius = 1.18;
  }
  // the fitted skull wins for placement: eyes where the sockets were, nose on the muzzle tip, mouth under it
  if (fit) { s.eyeY = 0; s.eyeSize = Math.min(s.eyeSize + 0.01, fit.eyeSize); s.noseY = fit.noseY; s.mouthY = fit.mouthY; s.eraseY = fit.eraseY; }
  if (modelId === 'fox-st') { s.naturalMuzzle = true; s.irisColor = [0.74, 0.47, 0.13]; s.eyeAspect = 0.64; s.lidHeavy = 0.14; s.browAngle = 0.18; }
  if (modelId === 'hare-st') { s.naturalMuzzle = true; s.irisColor = [0.36, 0.24, 0.12]; s.eyeAspect = 0.86; s.lidHeavy = 0.12; s.browAngle = -0.12; }
  return s;
}

/** Random face around a base (the model's fitted default): looks vary freely, placement (nose, mouth, mask) only a little. */
const PLACEMENT = new Set<keyof FaceShape>(['noseY', 'mouthY', 'eraseY', 'eraseRadius', 'eyeY']);
export function randomShape(rnd: () => number, base: FaceShape = DEFAULT_SHAPE): FaceShape {
  const s: FaceShape = { ...base };
  for (const sl of FACE_SLIDERS) {
    const mid = base[sl.key] as number;
    const spread = PLACEMENT.has(sl.key) ? 0.06 : 0.3;
    const r = !PLACEMENT.has(sl.key) && rnd() < 0.2 ? sl.min + rnd() * (sl.max - sl.min) : mid + (rnd() + rnd() - 1) * (sl.max - sl.min) * spread;
    (s[sl.key] as number) = Math.min(sl.max, Math.max(sl.min, r));
  }
  s.irisColor = [...IRIS_COLORS[Math.floor(rnd() * IRIS_COLORS.length)].rgb];
  s.noseColor = rnd() < 0.7 ? [0.86, 0.52, 0.5] : rnd() < 0.5 ? [0.2, 0.14, 0.12] : [0.6, 0.4, 0.36];
  s.derp = rnd() < 0.25 ? 0.3 + rnd() * 0.7 : 0;
  s.eraseRadius = base.eraseRadius;
  return s;
}
